/**
 * ApiReference — an interactive OpenAPI reference rendered by Scalar over the published
 * `openapi.json`. 100% client-side, credential-free, £0: the spec is a static asset
 * (website/public/openapi.json), Scalar is loaded from a pinned CDN bundle, and the whole
 * thing runs in the browser with no backend. Scalar's open-source API client respects the
 * per-operation `servers` in the spec, so each endpoint's "Try it" targets the WireMock
 * mock that holds it (the 2-way split — see the openapi-spec generator's MOCK_PARTS).
 *
 * SSR-safe: the server renders the container + a raw-spec fallback link; Scalar mounts on
 * the client in useEffect (it touches `document`/`window`, which don't exist during SSR).
 */
import { useEffect, useRef } from 'react';

/** Pinned so a CDN bump can't silently break the embed. `createApiReference` is the
 *  current (1.x) standalone init API. */
const SCALAR_CDN = 'https://cdn.jsdelivr.net/npm/@scalar/api-reference@1.58.0';

export interface ApiReferenceProps {
  /** URL of the OpenAPI document to render. Defaults to the published spec. */
  readonly specUrl?: string;
  /** Scalar request proxy — sidesteps browser CORS when "Try it" hits the hosted mocks. */
  readonly proxyUrl?: string;
}

interface ScalarGlobal {
  createApiReference: (target: Element | string, config: Record<string, unknown>) => void;
}

function mount(el: Element, specUrl: string, proxyUrl: string): boolean {
  const scalar = (window as unknown as { Scalar?: ScalarGlobal }).Scalar;
  if (!scalar?.createApiReference) return false;
  scalar.createApiReference(el, {
    url: specUrl,
    proxyUrl,
    hideClientButton: false,
    // Match the site's dark surface; Scalar still lets the reader toggle.
    darkMode: true,
  });
  return true;
}

export function ApiReference({ specUrl = '/openapi.json', proxyUrl = 'https://proxy.scalar.com' }: ApiReferenceProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Already loaded (client-side navigation back to this page) → mount immediately.
    if (mount(el, specUrl, proxyUrl)) return;

    // Reuse the script tag if a previous mount already added it; otherwise inject it.
    let script = document.querySelector<HTMLScriptElement>(`script[data-scalar="${SCALAR_CDN}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCALAR_CDN;
      script.async = true;
      script.dataset['scalar'] = SCALAR_CDN;
      document.body.appendChild(script);
    }
    const onLoad = () => mount(el, specUrl, proxyUrl);
    script.addEventListener('load', onLoad);
    return () => script?.removeEventListener('load', onLoad);
  }, [specUrl, proxyUrl]);

  return (
    <div className="min-h-[80vh] w-full">
      <div ref={ref} />
      <noscript>
        <p className="text-muted-foreground">
          This interactive reference needs JavaScript. The raw OpenAPI document is at{' '}
          <a className="underline" href={specUrl}>{specUrl}</a>.
        </p>
      </noscript>
    </div>
  );
}
